import * as React from "react"
import * as Antd from "antd"
import styles from "./RequestResetPasswordForm.module.scss"

import { AppRoutes } from "@constants/AppRoutes.ts"
import { useLocation, useNavigate } from "react-router-dom"
import { AppFunctions } from "@others/AppFunctions.ts"
import { useTranslation } from "react-i18next"
import { AppNotification } from "@others/AppNotification.ts"
import { IGetErrorApiDetail } from "@interfaces/IGetErrorApiDetail.ts"

import DvInput from "@components/dataEntry/DvInput/DvInput.tsx"
import AppText from "@components/general/AppText/AppText.tsx"
import DsButton from "@components/general/DsButton/DsButton.tsx"
import DvAntdForm from "@components/others/DvAntdForm/DvAntdForm.tsx"
import authService from "@services/authService/authService.ts"
import DvAntdFormItem from "@components/others/DvAntdFormItem/DvAntdFormItem.tsx"

export interface ILoginFormFieldType {
	email: string
}

interface IProps {
	className?: string
}

const RequestResetPasswordForm: React.FC<IProps> = (props) => {
	const { t } = useTranslation()

	const navigate = useNavigate()
	const location = useLocation()

	const [form] = Antd.Form.useForm<ILoginFormFieldType>()
	const [loginLoading, setLoginLoading] = React.useState(false)

	const [pageState, setPageState] = React.useState<"form" | "email-sent">(
		"form"
	)

	const onFinish = ({ email }: ILoginFormFieldType) => {
		setLoginLoading(true)

		const forward_to =
			AppFunctions.getCurrentDomain() + AppRoutes.resetPassword

		authService
			.requestResetPassword({
				email,
				forward_to,
			})
			.then(({ email_sent }) => {
				if (email_sent) {
					setPageState("email-sent")
				} else {
					AppNotification.error({
						message: t(
							"message_request_reset_password_header_failed"
						),
					})
				}
			})
			.catch(({ message, code }: IGetErrorApiDetail) => {
				switch (code) {
					case "EMAIL_REQUIRED":
						form.setFields([
							{
								name: "email",
								errors: [t("please_enter_your_email")],
							},
						])
						break
					case "INCORRECT_EMAIL_FORMAT":
						form.setFields([
							{
								name: "email",
								errors: [t("your_email_is_not_valid")],
							},
						])
						break
					case "EMAIL_NOT_FOUND":
						form.setFields([
							{
								name: "email",
								errors: [t("message_email_not_found")],
							},
						])
						break
					default:
						AppNotification.error({
							message: message || t("something_went_wrong"),
						})
				}
			})
			.finally(() => {
				setLoginLoading(false)
			})
	}

	React.useEffect(() => {
		if (location.state?.fromLoginEmailInputValue) {
			form.setFields([
				{
					name: "email",
					value: location.state.fromLoginEmailInputValue,
				},
			])
		}
	}, [])

	return (
		<DvAntdForm<ILoginFormFieldType>
			form={form}
			className={`${styles.login_form} ${props.className || ""}`}
			onFinish={onFinish}
		>
			<div className={styles.top}>
				<AppText
					className={styles.header}
					textKey={
						pageState === "form"
							? "message_request_reset_password_header"
							: "message_request_reset_password_header_sent"
					}
				/>

				{pageState === "form" && (
					<main className={styles.body}>
						<DvAntdFormItem<ILoginFormFieldType>
							label={t("email")}
							name="email"
							validateDebounce={1500}
							rules={[
								{
									required: true,
									message: t("please_enter_your_email"),
								},
								{
									type: "email",
									message: t("your_email_is_not_valid"),
								},
							]}
						>
							<DvInput placeholder={t("insert_your_email")} />
						</DvAntdFormItem>
					</main>
				)}
			</div>

			<div className={styles.footer}>
				{pageState === "form" && (
					<DsButton
						className={styles.action_buttons}
						htmlType="submit"
						loading={loginLoading}
						textKey="send"
					/>
				)}

				<DsButton
					className={styles.action_buttons}
					type="secondary"
					textKey="back"
					onClick={() => navigate(AppRoutes.login)}
				/>
			</div>
		</DvAntdForm>
	)
}

export default React.memo(RequestResetPasswordForm)
